import React from "react";
import {
  Box,
  Grid,
  TextField,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  FormLabel,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
} from "@mui/material";
import SectionCard from "./SectionCard";
import FormikTextField from "./FormikTextField";

const Step2ManufacturingFacility = ({
  formData,
  onFieldChange,
  errors,
  touched,
  setFieldValue,
  disable
}) => {
  const fieldError = (name) => Boolean(touched?.[name] && errors?.[name]);
  const fieldHelper = (name) => (touched?.[name] && errors?.[name]) || "";
  const fieldsEnabledQC = Boolean(disable?.isQCTeam || disable?.isAdmin);
  const fieldsEnabledHR = Boolean(disable?.isHRTeam || disable?.isAdmin);
  console.log(disable,"disable")

  const infrastructureParams = [
    { key: "totalSquareFeetDetails", label: "Total square feet" },
    { key: "totalBuildUpAreaDetails", label: "Total build up area" },
    { key: "totalVacantLandDetails", label: "Total vacant land" },
    { key: "roadAccess", label: "Road access" },
  ];

  const sufficiencyParams = [
    { key: "spaceForEachWorkstation", label: "Space for each workstation", group: "QC" },
    { key: "lightingArrangements", label: "Lighting arrangements", group: "QC" },
    { key: "airVentilation", label: "Air ventilation", group: "QC" },
    { key: "factoryCleanliness", label: "Factory cleanliness", group: "QC" },
    { key: "rawMaterialStorage", label: "Raw Material Storage", group: "QC" },
    { key: "machinerySafetyGuards", label: "Machinery safety guards", group: "QC" },
    { key: "materialHandling", label: "Material Handling", group: "QC" },
    { key: "packingAndDispatch", label: "Packing & Dispatch", group: "QC" },
    { key: "storageCapacity", label: "Storage capacity of finished products", group: "QC" },
    { key: "chemicalHazardControl", label: "Chemical hazard control", group: "HR" },
    { key: "electricityHazardControl", label: "Electricity hazard control", group: "HR" },
    { key: "powerSupplyOptions", label: "Power supply options", group: "HR" },
    { key: "effluentTreatmentPlan", label: "Effluent treatment plant (ETP)", group: "HR" },
  ];

  const qualityParams = [
    { key: "qualityPolicySop", label: "Quality Policy/SOP" },
    { key: "qualityPolicyDisplayed", label: "Quality Policy displayed" },
    { key: "randomQualityCheck", label: "Random Quality Check" },
    { key: "inspectionArea", label: "Inspection area with proper M/C's" },
    { key: "availabilityOfQcQa", label: "Availability of QC/QA" },
    { key: "testingLabFacilities", label: "Testing/Lab Facilities" },
  ];

  const statutoryParams = [
    { key: "factoryLicenceNumber", label: "Factory Licence (Number)" },
    { key: "employeesEnrolledEpf", label: "Number of employees enrolled in EPF and Establishment EPF code No." },
    { key: "employeesEnrolledEsi", label: "Number of employees enrolled in ESI / Establishment ESI No." },
  ];

  const healthSafetyParams = [
    { key: "healthAndSafety", label: "Policy on health and safety" },
    { key: "availabilityFirefightingEquipment", label: "Availability of firefighting equipment" },
    { key: "fireAlarmAudible", label: "Presence of a fire alarm audible throughout the production site" },
    { key: "emergencyExit", label: "Provision of emergency exits in the workplace" },
  ];

  const handleYesNoChange = (name, value) => {
    onFieldChange({ target: { name, value } });
  };

  const handleCheckboxChange = (paramKey, value) => {
    const currentValue = formData[paramKey];
    const newValue = currentValue === value ? "" : value;
   setFieldValue(paramKey, newValue);
  };

  return (
    <Box>
      <SectionCard title="1. Does the supplier/ vendor have infrastructure / build-up space for better service">
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControl component="fieldset">
              <FormLabel component="legend">Infrastructure Available</FormLabel>
              <RadioGroup
                row
                name="infrastructureAvailable"
                value={formData.infrastructureAvailable || ""}
                onChange={(e) => handleYesNoChange("infrastructureAvailable", e.target.value)}
              >
                <FormControlLabel value="Y" control={<Radio />} label="Yes" disabled={!fieldsEnabledHR} />
                <FormControlLabel value="N" control={<Radio />} label="No" disabled={!fieldsEnabledHR} />
              </RadioGroup>
              {touched.infrastructureAvailable && errors.infrastructureAvailable && (
                <Typography color="error" variant="caption">{errors.infrastructureAvailable}</Typography>
              )}
            </FormControl>
          </Grid>
          {formData.infrastructureAvailable === "Y" && (
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>a. If Yes:</Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Infrastructure</TableCell>
                      <TableCell>Details</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {infrastructureParams.map((param) => (
                      <TableRow key={param.key}>
                        <TableCell>{param.label}</TableCell>
                        <TableCell>
                          <TextField
                            fullWidth
                            size="small"
                            name={param.key}
                            value={formData[param.key] || ""}
                            onChange={onFieldChange}
                            error={fieldError(param.key)}
                            helperText={fieldHelper(param.key)}
                         disabled={!fieldsEnabledHR}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          )}
        </Grid>
      </SectionCard>

      <SectionCard title="2. Does the supplier/ vendor have adequate machinery and equipment to supply materials/ services">
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControl component="fieldset">
              <FormLabel component="legend">Machinery Available</FormLabel>
              <RadioGroup
                row
                name="machineryAvailable"
                value={formData.machineryAvailable || ""}
                onChange={(e) => handleYesNoChange("machineryAvailable", e.target.value)}
              >   
                <FormControlLabel value="Y" control={<Radio />} label="Yes" disabled={!fieldsEnabledQC} />
                <FormControlLabel value="N" control={<Radio />} label="No" disabled={!fieldsEnabledQC} />
              </RadioGroup>
              {touched.machineryAvailable && errors.machineryAvailable && (
                <Typography color="error" variant="caption">{errors.machineryAvailable}</Typography>
              )}
            </FormControl>
          </Grid>
          {formData.machineryAvailable === "N" && (
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>a. If No:</Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>SI</TableCell>
                      <TableCell>Descriptions</TableCell>
                      <TableCell>No</TableCell>
                      <TableCell>Status of maintenance</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {[1, 2, 3, 4, 5].map((idx) => (
                      <TableRow key={idx}>
                        <TableCell>{idx}</TableCell>
                        <TableCell>
                          <TextField disabled={!fieldsEnabledQC} fullWidth size="small" name={`machineryDesc_${idx}`} value={formData[`machineryDesc_${idx}`] || ""} onChange={onFieldChange} />
                        </TableCell>
                        <TableCell>
                          <TextField disabled={!fieldsEnabledQC} fullWidth size="small" type="number" name={`machineryNo_${idx}`} value={formData[`machineryNo_${idx}`] || ""} onChange={onFieldChange} />
                        </TableCell>
                        <TableCell>
                          <TextField disabled={!fieldsEnabledQC} fullWidth size="small" name={`machineryStatus_${idx}`} value={formData[`machineryStatus_${idx}`] || ""} onChange={onFieldChange} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          )}
        </Grid>
      </SectionCard>

      <SectionCard title="3. Does the supplier/ vendor have adequate knowledge of the manufacturing processes carried out by him">
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControl component="fieldset">
              <RadioGroup row name="adequateKnowledgeManufacturingProcess" value={formData.adequateKnowledgeManufacturingProcess || ""} onChange={(e) => handleYesNoChange("adequateKnowledgeManufacturingProcess", e.target.value)}>
                <FormControlLabel value="Y" control={<Radio />} label="Yes" disabled={!fieldsEnabledQC} />
                <FormControlLabel value="N" control={<Radio />} label="No" disabled={!fieldsEnabledQC} />
              </RadioGroup>
              {touched.adequateKnowledgeManufacturingProcess && errors.adequateKnowledgeManufacturingProcess && (
                <Typography color="error" variant="caption">{errors.adequateKnowledgeManufacturingProcess}</Typography>
              )}
            </FormControl>
            <FormikTextField disabled={!fieldsEnabledQC} multiline rows={2} placeholder="Comments" name="adequateKnowledgeRemarks" sx={{ mt: 1 }} />
          </Grid>
        </Grid>
      </SectionCard>

      <SectionCard title="4. Is record of raw materials and process consumables maintained">
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControl component="fieldset">
              <RadioGroup row name="recordOfRawmaterialsMaintained" value={formData.recordOfRawmaterialsMaintained || ""} onChange={(e) => handleYesNoChange("recordOfRawmaterialsMaintained", e.target.value)}>
                <FormControlLabel value="Y" disabled={!fieldsEnabledQC} control={<Radio />} label="Yes" />
                <FormControlLabel value="N" disabled={!fieldsEnabledQC} control={<Radio />} label="No" />
              </RadioGroup>
              {touched.recordOfRawmaterialsMaintained && errors.recordOfRawmaterialsMaintained && (
                <Typography color="error" variant="caption">{errors.recordOfRawmaterialsMaintained}</Typography>
              )}
            </FormControl>
            <FormikTextField disabled={!fieldsEnabledQC} multiline rows={2} placeholder="Comments" name="rawmaterialsMaintainedRemarks" sx={{ mt: 1 }} />
          </Grid>
        </Grid>
      </SectionCard>

      <SectionCard title="5. Does the supplier/ vendor have trained manpower to carry out the manufacturing processes">
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControl component="fieldset">
              <RadioGroup row name="trainedManpower" value={formData.trainedManpower || ""} onChange={(e) => handleYesNoChange("trainedManpower", e.target.value)}>
                <FormControlLabel disabled={!fieldsEnabledQC} value="Y" control={<Radio />} label="Yes" />
                <FormControlLabel disabled={!fieldsEnabledQC} value="N" control={<Radio />} label="No" />
              </RadioGroup>
              {touched.trainedManpower && errors.trainedManpower && (
                <Typography color="error" variant="caption">{errors.trainedManpower}</Typography>
              )}
            </FormControl>
            <FormikTextField disabled={!fieldsEnabledQC} multiline rows={2} placeholder="Comments" name="trainedManpowerRemarks" sx={{ mt: 1 }} />
          </Grid>
        </Grid>
      </SectionCard>

      <SectionCard title="6. Is the working area designed according to process flow?">
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControl component="fieldset">
              <RadioGroup row name="workingAreaDesigned" value={formData.workingAreaDesigned || ""} onChange={(e) => handleYesNoChange("workingAreaDesigned", e.target.value)}>
                <FormControlLabel disabled={!fieldsEnabledQC} value="Y" control={<Radio />} label="Yes" />
                <FormControlLabel disabled={!fieldsEnabledQC} value="N" control={<Radio />} label="No" />
              </RadioGroup>
              {touched.workingAreaDesigned && errors.workingAreaDesigned && (
                <Typography color="error" variant="caption">{errors.workingAreaDesigned}</Typography>
              )}
            </FormControl>
            <FormikTextField disabled={!fieldsEnabledQC} multiline rows={2} placeholder="Comments" name="workingAreaDesignedRemarks" sx={{ mt: 1 }} />
          </Grid>
        </Grid>
      </SectionCard>

      <SectionCard title="7. Are workstations designed to facilitate the process flow and manufacturing requirements">
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControl component="fieldset">
              <RadioGroup row name="workstationsDesigned" value={formData.workstationsDesigned || ""} onChange={(e) => handleYesNoChange("workstationsDesigned", e.target.value)}>
                <FormControlLabel disabled={!fieldsEnabledQC} value="Y" control={<Radio />} label="Yes" />
                <FormControlLabel disabled={!fieldsEnabledQC} value="N" control={<Radio />} label="No" />
              </RadioGroup>
              {touched.workstationsDesigned && errors.workstationsDesigned && (
                <Typography color="error" variant="caption">{errors.workstationsDesigned}</Typography>
              )}
            </FormControl>
            <FormikTextField disabled={!fieldsEnabledQC} multiline rows={2} placeholder="Comments" name="workstationsDesignedRemarks" sx={{ mt: 1 }} />
          </Grid>
        </Grid>
      </SectionCard>

      <SectionCard title="8. Financial Strength">
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Si No</TableCell>
                    <TableCell>Parameters</TableCell>
                    <TableCell>Yes/No</TableCell>
                    <TableCell>Comments</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[
                    { key: "financialyStrongEnough", label: "Is the supplier/vendor financially strong enough to manage a secure supply chain" },
                    { key: "maintainBankAccount", label: "Does the supplier/vendor maintain a bank account and accept payments through bank transfer" },
                    { key: "extendSupplyOnCredit", label: "Does the supplier/vendor in a position to extend supplies on credit" },
                  ].map((param, idx) => (
                    <TableRow key={param.key}>
                      <TableCell>{idx + 1}</TableCell>
                      <TableCell>{param.label}</TableCell>
                      <TableCell>
                        <RadioGroup row name={param.key} value={formData[param.key] || ""} onChange={(e) => handleYesNoChange(param.key, e.target.value)}>
                          <FormControlLabel disabled={!fieldsEnabledQC} value="Y" control={<Radio />} label="Yes" />
                          <FormControlLabel disabled={!fieldsEnabledQC} value="N" control={<Radio />} label="No" />
                        </RadioGroup>
                      </TableCell>
                      <TableCell>
                        <TextField disabled={!fieldsEnabledQC} fullWidth size="small" name={`${param.key}Remarks`} value={formData[`${param.key}Remarks`] || ""} onChange={onFieldChange} error={fieldError(`${param.key}Remarks`)} helperText={fieldHelper(`${param.key}Remarks`)} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        </Grid>
      </SectionCard>

      <SectionCard title="9. Please indicate the sufficiency of the following">
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Si.No</TableCell>
                    <TableCell>Parameters</TableCell>
                    <TableCell align="center">Adequate</TableCell>
                    <TableCell align="center">Available</TableCell>
                    <TableCell align="center">Need improve</TableCell>
                    <TableCell align="center">Not available</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sufficiencyParams.map((param, idx) => {
                    const isDisabled =
                        (param.group === "QC" && !fieldsEnabledQC) ||
                        (param.group === "HR" && !fieldsEnabledHR);
                        return (
                    <TableRow key={param.key}>
                      <TableCell>{idx + 1}</TableCell>
                      <TableCell>{param.label}</TableCell>
                      {["Adequate", "Available", "Need improve", "Not available"].map((status) => (
                        <TableCell key={status} align="center">
                          <Checkbox disabled={isDisabled} checked={formData[param.key] === status} onChange={() => handleCheckboxChange(param.key, status)} />
                        </TableCell>
                      ))}
                    </TableRow>
                        )
})}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        </Grid>
      </SectionCard>

      <SectionCard title="10. Quality Management System">
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Si.No</TableCell>
                    <TableCell>Parameters</TableCell>
                    <TableCell align="center">Adequate</TableCell>
                    <TableCell align="center">Available</TableCell>
                    <TableCell align="center">Need improve</TableCell>
                    <TableCell align="center">Not available</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {qualityParams.map((param, idx) => (
                    <TableRow key={param.key}>
                      <TableCell>{idx + 1}</TableCell>
                      <TableCell>{param.label}</TableCell>
                      {["Adequate", "Available", "Need improve", "Not available"].map((status) => (
                        <TableCell key={status} align="center">
                          <Checkbox disabled={!fieldsEnabledQC} checked={formData[param.key] === status} onChange={() => handleCheckboxChange(param.key, status)} />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        </Grid>
      </SectionCard>

      <SectionCard title="11. Human Resource Management">
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth disabled={!fieldsEnabledHR} label="a. What is the minimum age limit of employee work in factory?" name="minimumAgeLimit" value={formData.minimumAgeLimit || ""} onChange={onFieldChange} size="small" error={fieldError("minimumAgeLimit")} helperText={fieldHelper("minimumAgeLimit")} />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>b. Employee count</Typography>
            <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Staff head count</TableCell>
                    <TableCell>Labour head count</TableCell>
                    <TableCell>Total Head count</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell><TextField disabled={!fieldsEnabledHR} fullWidth size="small" type="number" name="staffHeadCount" value={formData.staffHeadCount || ""} onChange={onFieldChange} /></TableCell>
                    <TableCell><TextField disabled={!fieldsEnabledHR} fullWidth size="small" type="number" name="labourHeadCount" value={formData.labourHeadCount || ""} onChange={onFieldChange} /></TableCell>
                    <TableCell><TextField disabled={!fieldsEnabledHR} fullWidth size="small" type="number" name="totalHeadCount" value={formData.totalHeadCount || ""} onChange={onFieldChange} /></TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>c. Nature of employment</Typography>
            <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Si No</TableCell>
                    <TableCell>Employment</TableCell>
                    <TableCell>Head count</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[{ key: "permanent", label: "Permanent" }, { key: "contract", label: "Contract" }, { key: "temperory", label: "Temporary" }].map((emp, idx) => (
                    <TableRow key={emp.key}>
                      <TableCell>{idx + 1}</TableCell>
                      <TableCell>{emp.label}</TableCell>
                      <TableCell><TextField disabled={!fieldsEnabledHR} fullWidth size="small" type="number" name={`${emp.key}HeadCount`} value={formData[`${emp.key}HeadCount`] || ""} onChange={onFieldChange} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>d. Working hours and wages payment</Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Si No</TableCell>
                    <TableCell>Parameters</TableCell>
                    <TableCell>Details</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[
                    { key: "standardWorkingHours", label: "Standard working hours (excluding overtime) in a day" },
                    { key: "shiftsPerDay", label: "Shifts per day plant normally work" },
                    { key: "systemOfPayment", label: "System of payment for labour (Piece rate, shift wages, per day wages, monthly wages)" },
                    { key: "workersGetPaid", label: "How often workers get paid (pay period)" },
                    { key: "paidSickAndAnnualLeave", label: "Whether workers receive paid sick leave and paid annual leave" },
                    { key: "accomadation", label: "Whether accommodation is provided for workers" },
                    { key: "noOfMigrantWorkers", label: "Number of migrant workers employed" },
                  ].map((param, idx) => (
                    <TableRow key={param.key}>
                      <TableCell>{idx + 1}</TableCell>
                      <TableCell>{param.label}</TableCell>
                      <TableCell><TextField disabled={!fieldsEnabledHR} fullWidth size="small" name={param.key} value={formData[param.key] || ""} onChange={onFieldChange} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        </Grid>
      </SectionCard>

      <SectionCard title="12. Statutory Factory compliance">
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Si No</TableCell>
                    <TableCell>Parameters</TableCell>
                    <TableCell>Yes/No</TableCell>
                    <TableCell>Comments</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {statutoryParams.map((param, idx) => (
                    <TableRow key={param.key}>
                      <TableCell>{idx + 1}</TableCell>
                      <TableCell>{param.label}</TableCell>
                      <TableCell>
                        <RadioGroup row name={param.key} value={formData[param.key] || ""} onChange={(e) =>
            handleYesNoChange(param.key, e.target.value)
          }>
                          <FormControlLabel disabled={!fieldsEnabledHR} value="Y" control={<Radio />} label="Yes" />
                          <FormControlLabel disabled={!fieldsEnabledHR} value="N" control={<Radio />} label="No" />
                        </RadioGroup>
                      </TableCell>
                      <TableCell>
                        <TextField fullWidth disabled={!fieldsEnabledHR} size="small" name={`${param.key}Remarks`} value={formData[`${param.key}Remarks`] || ""} onChange={onFieldChange} error={fieldError(`${param.key}Remarks`)} helperText={fieldHelper(`${param.key}Remarks`)} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        </Grid>
      </SectionCard>

      <SectionCard title="13. Health and Safety Management">
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Si No</TableCell>
                    <TableCell>Parameters</TableCell>
                    <TableCell>Yes/No</TableCell>
                    <TableCell>Comments</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {healthSafetyParams.map((param, idx) => (
                    <TableRow key={param.key}>
                      <TableCell>{idx + 1}</TableCell>
                      <TableCell>{param.label}</TableCell>
                      <TableCell>
                        <RadioGroup row name={param.key} value={formData[param.key] || ""} onChange={(e) =>
            handleYesNoChange(param.key, e.target.value)
          }>
                          <FormControlLabel disabled={!fieldsEnabledHR} value="Y" control={<Radio />} label="Yes" />
                          <FormControlLabel disabled={!fieldsEnabledHR} value="N" control={<Radio />} label="No" />
                        </RadioGroup>
                      </TableCell>
                      <TableCell>
                        <TextField disabled={!fieldsEnabledHR} fullWidth size="small" name={`${param.key}Remarks`} value={formData[`${param.key}Remarks`] || ""} onChange={onFieldChange} error={fieldError(`${param.key}Remarks`)} helperText={fieldHelper(`${param.key}Remarks`)} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        </Grid>
      </SectionCard>
    </Box>
  );
};

export default Step2ManufacturingFacility;
